"use client";
// @ts-strict-ignore
import * as React from "react";
import styled from "styled-components";
import { Colors } from "themes/color-palette";
import { StyledButton } from "./style";

export interface Props extends React.HTMLProps<HTMLButtonElement> {
  children: React.ReactNode;
  disabled?: boolean;
  hiddenBefore?: number;
  hideOnMobile?: boolean;
  id?: string;
  isBlue?: boolean;
  isCryptopedia?: boolean;
  isDarkTheme?: boolean;
  isTransparent?: boolean;
  isBlack?: boolean;
  isGrey?: boolean;
  isBold?: boolean;
  isLight?: boolean;
  isOverlayOpen?: boolean;
  isPrimary?: boolean;
  isSmall?: boolean;
  isWhite?: boolean;
  onClick?: (event?) => void;
  style?: React.CSSProperties;
  tabIndex?: number;
  type?: string;
}

type ButtomTheme = {
  textColor: string;
  borderColor: string;
  backgroundColor: string;
  hoverBorderColor: string;
  hoverTextColor: string;
  borderRadius?: string;
  hoverBackgroundColor: string;
  clickBorderColor: string;
  clickTextColor: string;
  clickBackgroundColor: string;
};
// needs hubble tokens
const buttonThemeColors = (
  isWhite = false,
  isDarkTheme = false,
  isPrimary = false,
  isBlue = false,
  isCryptopedia = false,
  isLight = false,
  isGrey = false,
  isTransparent = false,
  isBlack = false
): ButtomTheme => {
  if (isWhite) {
    return {
      textColor: "#010304",
      borderColor: Colors.black,
      borderRadius: "999px",
      backgroundColor: Colors.white,
      hoverBorderColor: Colors.black,
      hoverTextColor: "#010304",
      hoverBackgroundColor: Colors.white,
      clickBorderColor: Colors.black,
      clickTextColor: "#010304",
      clickBackgroundColor: Colors.white
    };
  } else if (isDarkTheme) {
    return {
      textColor: Colors.white,
      borderColor: Colors.white,
      borderRadius: "999px",
      backgroundColor: "initial",
      hoverBorderColor: Colors.white,
      hoverTextColor: Colors.white,
      hoverBackgroundColor: "initial",
      clickBorderColor: Colors.white,
      clickTextColor: Colors.white,
      clickBackgroundColor: "initial"
    };
  } else if (isLight) {
    return {
      textColor: Colors.black,
      borderColor: Colors.black,
      borderRadius: "999px",
      backgroundColor: "initial",
      hoverBorderColor: Colors.gray["900"],
      hoverTextColor: Colors.gray["900"],
      hoverBackgroundColor: "initial",
      clickBorderColor: Colors.gray["800"],
      clickTextColor: Colors.gray["800"],
      clickBackgroundColor: "initial"
    };
  } else if (isPrimary) {
    return {
      textColor: Colors.white,
      borderColor: Colors.black,
      borderRadius: "999px",
      backgroundColor: Colors.black,
      hoverBorderColor: Colors.gray["900"],
      hoverTextColor: Colors.white,
      hoverBackgroundColor: Colors.gray["900"],
      clickBorderColor: Colors.gray["800"],
      clickTextColor: Colors.white,
      clickBackgroundColor: Colors.gray["800"]
    };
  } else if (isBlue) {
    return {
      textColor: Colors.black,
      borderColor: Colors.cyan,
      borderRadius: "999px",
      backgroundColor: Colors.cyan,
      hoverBorderColor: "#7CEAFB",
      hoverTextColor: Colors.black,
      hoverBackgroundColor: "#7CEAFB",
      clickBorderColor: "#A8F1FC",
      clickTextColor: Colors.black,
      clickBackgroundColor: "#A8F1FC"
    };
  } else if (isCryptopedia) {
    return {
      textColor: Colors.white,
      borderColor: Colors.black,
      borderRadius: "999px",
      backgroundColor: Colors.black,
      hoverBorderColor: Colors.gray["900"],
      hoverTextColor: Colors.white,
      hoverBackgroundColor: Colors.gray["900"],
      clickBorderColor: Colors.gray["800"],
      clickTextColor: Colors.white,
      clickBackgroundColor: Colors.gray["800"]
    };
  } else if (isGrey) {
    return {
      textColor: Colors.black,
      borderColor: "#e4e4e4",
      borderRadius: "999px",
      backgroundColor: "#e4e4e4",
      hoverBorderColor: "#e4e4e4",
      hoverTextColor: Colors.black,
      hoverBackgroundColor: "#e4e4e4",
      clickBorderColor: "#e4e4e4",
      clickTextColor: Colors.black,
      clickBackgroundColor: "#e4e4e4"
    };
  } else if (isTransparent) {
    return {
      textColor: Colors.white,
      borderColor: "transparent",
      borderRadius: "999px",
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      hoverBorderColor: "transparent",
      hoverTextColor: Colors.white,
      hoverBackgroundColor: "rgba(255, 255, 255, 0.12)",
      clickBorderColor: "transparent",
      clickTextColor: Colors.white,
      clickBackgroundColor: "rgba(255, 255, 255, 0.12)"
    };
  } else if (isBlack) {
    return {
      textColor: Colors.black,
      borderColor: "transparent",
      borderRadius: "999px",
      backgroundColor: "rgba(1, 3, 4, 0.08)",
      hoverBorderColor: "transparent",
      hoverTextColor: Colors.black,
      hoverBackgroundColor: "rgba(1, 3, 4, 0.08)",
      clickBorderColor: "transparent",
      clickTextColor: Colors.black,
      clickBackgroundColor: "rgba(1, 3, 4, 0.08)"
    };
  }
};

const Button: React.FC<Props> = ({
  children,
  isPrimary,
  isOverlayOpen,
  isBlue,
  isWhite,
  isGrey,
  isSmall,
  isCryptopedia,
  hideOnMobile,
  isDarkTheme,
  isTransparent,
  isBlack,
  isBold,
  isLight,
  style,
  hiddenBefore,
  tabIndex,
  onClick,
  type,
  disabled,
  ...props
}) => {
  const buttonTheme = buttonThemeColors(
    isWhite,
    isDarkTheme,
    isPrimary,
    isBlue,
    isCryptopedia,
    isLight,
    isGrey,
    isTransparent,
    isBlack
  ) || {
    textColor: "",
    borderColor: "",
    backgroundColor: "",
    hoverBorderColor: "",
    hoverTextColor: "",
    hoverBackgroundColor: ""
  };

  return (
    <StyledButton
      buttonTheme={buttonTheme}
      hideOnMobile={hideOnMobile}
      isOverlayOpen={isOverlayOpen}
      isSmall={isSmall}
      isBold={isBold}
      style={style}
      hiddenBefore={hiddenBefore}
      tabIndex={tabIndex}
      onClick={onClick}
      type={type}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

export const HTMLButton = styled.button.attrs(() => ({
  type: "button"
}))`
  margin-top: 3px;
  width: 25px !important;
  cursor: pointer;
  padding: 0;
  border: 0;
  background: none;
`;

export const HTMLButtonMainNav = styled.button.attrs(() => ({
  type: "button"
}))`
  cursor: pointer;
  padding: 0;
  border: 1px solid transparent;
  background: none;
  line-height: 0.8;
`;
export default Button;
